import React from 'react';
import Icon from "components/Icon";
import IconName from "types/IconName";
import {concatenate} from "services/string";

enum Size {SMALL, LARGE}

enum Color {BRAND, BLUE, GREEN, YELLOW, RED, GRAY}

type Props = {
    size: Size
    color: Color
    text: string
    icon: IconName | string
    onDismiss?: () => any
}

const badgeSizeClasses: Record<Size, string> = {
    [Size.SMALL]: 'py-0.5 px-2 text-xs',
    [Size.LARGE]: 'py-0.5 px-2.5 text-sm',
}

const badgeColorClasses: Record<Color, string> = {
    [Color.BRAND]: 'bg-brand/20 text-brand',
    [Color.BLUE]: 'bg-blue-100 text-blue-800',
    [Color.GREEN]: 'bg-green-100 text-green-800',
    [Color.YELLOW]: 'bg-yellow-100 text-yellow-800',
    [Color.RED]: 'bg-red-100 text-red-800',
    [Color.GRAY]: 'bg-gray-100 text-gray-800',
}

const buttonColorClasses: Record<Color, string> = {
    [Color.BRAND]: 'text-brand hover:bg-brand hover:text-white focus:bg-brand',
    [Color.BLUE]: 'text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:bg-blue-500',
    [Color.GREEN]: 'text-green-400 hover:bg-green-200 hover:text-green-500 focus:bg-green-500',
    [Color.YELLOW]: 'text-yellow-400 hover:bg-yellow-200 hover:text-yellow-500 focus:bg-yellow-500',
    [Color.RED]: 'text-red-400 hover:bg-red-200 hover:text-red-500 focus:bg-red-500',
    [Color.GRAY]: 'text-gray-400 hover:bg-gray-200 hover:text-gray-500 focus:bg-gray-500',
}

function IconBadge(props: Props) {
    const {size, color, text, icon, onDismiss} = props

    return (
        <span className={concatenate(badgeSizeClasses[size], badgeColorClasses[color], 'mr-3 mb-1 inline-flex rounded-md items-center font-medium')}>
            {/* Icon */}
            <Icon name={icon} className="h-4 w-4 mr-1" />

            {/* Text */}
            {text}

            {/* Dismiss Button */}
            {onDismiss?
                <button
                    type="button"
                    onClick={onDismiss}
                    className={concatenate(
                        buttonColorClasses[color],
                        'flex-shrink-0 ml-1 -mr-1 h-4 w-4 rounded-full inline-flex items-center justify-center focus:outline-none focus:text-white'
                    )}>
                    <span className="sr-only">Deselect fact</span>
                    <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                        <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                    </svg>
                </button> : null}
        </span>
    );
}

IconBadge.sizes = Size
IconBadge.colors = Color

IconBadge.defaultProps = {
    size: Size.SMALL,
    color: Color.GRAY,
    icon: IconName.INFORMATION_CIRCLE
}

export default IconBadge;